import { useState, type FormEvent } from "react";
import { asDecimalString, Money } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, type DataTableColumn, usePagination, useSortableData } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createExpense, listExpenses } from "../../api/expensesApi";
import { EXPENSE_CATEGORIES, type ExpenseCategory, type ExpenseOut } from "../../api/types";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { BranchSelect } from "../../components/BranchSelect";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — same documented stand-in every
// other money-displaying page in this app uses (see e.g. DashboardPage.tsx).

const CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  RENT: "Rent",
  SALARIES: "Staff Salaries",
  UTILITIES: "Utilities",
  SUPPLIES: "Supplies",
  OTHER: "Other",
};

/** Simplified-dashboard "Add Expense" (business-owner walkthrough follow-up). Unlike
 * Invoices & Bills' VENDOR_BILL (an owed-to-vendor amount tracked separately), every
 * expense logged here immediately posts a real ledger entry — it shows up in Profit &
 * Loss right away (`POST /expenses`, `app/services/expense_service.py`). */
export function ExpensesPage() {
  const { ensureFreshToken } = useAuth();
  const [categoryFilter, setCategoryFilter] = useState<ExpenseCategory | "">("");
  const [recurringFilter, setRecurringFilter] = useState<"" | "true" | "false">("");
  const expensesState = useAsyncData(
    () =>
      ensureFreshToken().then((token) =>
        listExpenses(token, {
          category: categoryFilter || undefined,
          isRecurring: recurringFilter === "" ? undefined : recurringFilter === "true",
        }),
      ),
    [categoryFilter, recurringFilter],
  );

  const { sortedData, sort, requestSort } = useSortableData<ExpenseOut, keyof ExpenseOut>(
    expensesState.data ?? [],
    (row, key) => String(row[key]),
  );
  const { pageData, pagination, setPage } = usePagination(sortedData, 25);

  const [category, setCategory] = useState<ExpenseCategory>("RENT");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [branchId, setBranchId] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceInterval, setRecurrenceInterval] = useState("Monthly");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createExpense(token, {
        branch_id: branchId || null,
        category,
        description,
        amount: asDecimalString(amount),
        expense_date: expenseDate,
        is_recurring: isRecurring,
        recurrence_interval: isRecurring ? recurrenceInterval : null,
      });
      setDescription("");
      setAmount("");
      expensesState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not save this expense.");
    } finally {
      setSubmitting(false);
    }
  }

  const columns: DataTableColumn<ExpenseOut, keyof ExpenseOut>[] = [
    { key: "expense_date", header: "Date", sortable: true, render: (r) => r.expense_date },
    { key: "category", header: "Category", sortable: true, render: (r) => CATEGORY_LABELS[r.category as ExpenseCategory] ?? r.category },
    { key: "description", header: "Description", render: (r) => r.description },
    { key: "amount", header: "Amount", sortable: true, align: "right", render: (r) => Money.format(r.amount, TENANT_CURRENCY_FALLBACK) },
    {
      key: "is_recurring",
      header: "Type",
      render: (r) => (
        <span className={`badge ${r.is_recurring ? "badge--current" : "badge--paid"}`}>
          {r.is_recurring ? `Routine${r.recurrence_interval ? ` (${r.recurrence_interval})` : ""}` : "One-off"}
        </span>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Expenses</h1>
          <p className="page__subtitle">
            What the business spent — rent, salaries, and everything else. Every entry here counts toward Profit &amp; Loss immediately.
          </p>
        </div>
      </div>

      {expensesState.error ? <StatusBanner kind="error">{expensesState.error}</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">Add expense</h2>
        <form onSubmit={handleCreate}>
          <div className="form-grid">
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="exp-category">
                Category
              </label>
              <select
                id="exp-category"
                className="sl-input"
                value={category}
                onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
              >
                {EXPENSE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </div>
            <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} required />
            <Input label="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" required />
            <Input label="Date" type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} required />
            <BranchSelect id="exp-branch" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="Enterprise-wide (no branch)" />
          </div>
          <div className="sl-field" style={{ marginBottom: "14px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 400 }}>
              <input type="checkbox" checked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)} />
              This is a routine expense (e.g. monthly rent or salaries)
            </label>
            {isRecurring ? (
              <div style={{ marginTop: "8px", maxWidth: "220px" }}>
                {/* A fixed set of intervals, not free text (business-owner request) — the
                    backend stores recurrence_interval as an opaque string, so this only
                    constrains what NEW entries say; anything already saved renders as-is. */}
                <label className="sl-field">
                  <span className="sl-field__label">How often</span>
                  <select
                    className="sl-input"
                    value={recurrenceInterval}
                    onChange={(e) => setRecurrenceInterval(e.target.value)}
                  >
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                </label>
              </div>
            ) : null}
          </div>
          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Add expense"}
            </Button>
          </div>
        </form>
      </div>

      <div className="panel">
        <div className="filter-bar">
          <div className="sl-field">
            <label className="sl-field__label" htmlFor="exp-category-filter">
              Category
            </label>
            <select
              id="exp-category-filter"
              className="sl-input"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as ExpenseCategory | "")}
            >
              <option value="">All categories</option>
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </div>
          <div className="sl-field">
            <label className="sl-field__label" htmlFor="exp-recurring-filter">
              Type
            </label>
            <select
              id="exp-recurring-filter"
              className="sl-input"
              value={recurringFilter}
              onChange={(e) => setRecurringFilter(e.target.value as "" | "true" | "false")}
            >
              <option value="">All</option>
              <option value="true">Routine only</option>
              <option value="false">One-off only</option>
            </select>
          </div>
        </div>
        {expensesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={pageData}
            getRowId={(row) => row.id}
            sort={sort}
            onSortChange={requestSort}
            pagination={pagination}
            onPageChange={setPage}
            emptyMessage="No expenses logged yet."
            caption="Expenses"
          />
        )}
      </div>
    </div>
  );
}
